import { reportQueries, useReportStore } from '@/entities/report';
import { createFileRoute } from '@tanstack/react-router';
import { ReportTemplate, TabMenu } from '@/shared/ui';
import { ReportDetailHeader } from '@/widgets/section-header';
import { useCallback, useEffect } from 'react';

import {
    ReportStntContentsViewer,
    ReportStntThumbList,
} from '@/widgets/report/detail/table-body/stnt';
import { ReportHeaderLeft, ReportHeaderStntRight } from '@/widgets/report/detail/table-header';
import {
    reportMainTabStateTypeGuard,
    reportSubTabStateTypeGuard,
} from '@/entities/report/model/report.types';
import { usePublishedBoxStore } from '@/entities/sets/publish';
import { AiReview } from '@/widgets/report/ai-review';

export const Route = createFileRoute('/report/stnt/')({
    component: RouteComponent,
});

function RouteComponent() {
    const { stntReport, setStntReport, resetAll } = useReportStore();
    const { setTab } = usePublishedBoxStore();
    const targetId = stntReport.targetId ?? 0;
    const stntId = stntReport.stntId ?? '';
    const nickname = stntReport?.nickname ?? '';

    const { data, isLoading, refetch } = reportQueries.stnt.getStntSrchEvalResultDetail({
        evlId: targetId,
        stntId: stntId,
    });

    useEffect(() => {
        return () => {
            setStntReport({ mainTab: '상세 보기' });
            setTab(stntReport?.publishTab ?? '발행 리스트별');
        };
    }, []);

    const refetchDetailHandler = () => {
        refetch();
    };

    // 메인 탭 변경
    const mainTabChange = useCallback(
        (val: string) => {
            if (reportMainTabStateTypeGuard(val)) {
                setStntReport({ mainTab: val });
            }
        },
        [setStntReport]
    );

    // 서브 탭 변경
    const subTabChange = useCallback(
        (val: string) => {
            if (reportSubTabStateTypeGuard(val)) {
                // 콘텐츠 정보 클릭 시 학생 index 초기화
                if (val === 'contents') {
                    setStntReport({ stntsListIdx: undefined });
                    if (stntReport.thumbListIdx === undefined) {
                        setStntReport({ thumbListIdx: 0 });
                    }
                }
                setStntReport({ subTab: val });
            }
        },
        [setStntReport, stntReport.thumbListIdx]
    );

    // 썸네일 리스트 index 변경
    const tumbListChange = useCallback(
        (idx: number) => {
            setStntReport({ thumbListIdx: idx });
        },
        [setStntReport]
    );

    // 학생 index 변경
    const studentChange = useCallback(
        (idx: number) => {
            setStntReport({ stntsListIdx: idx });
        },
        [setStntReport]
    );

    if (isLoading) return null;
    if (!data) return null;

    const scoreType = data?.evlStdr;
    const score = data?.stntTotScr;
    const submAt = data?.evlInfoList?.[0]?.mdulEvlInfo?.submAt;

    const selectedModule =
        stntReport.thumbListIdx === 0 || stntReport.thumbListIdx
            ? data?.evlInfoList[stntReport.thumbListIdx ?? 0]
            : undefined;
    const contentsNum =
        stntReport.thumbListIdx === 0 || stntReport.thumbListIdx ? stntReport.thumbListIdx + 1 : 1;

    return (
        <div className="contwrap">
            <div className="contInner">
                <ReportDetailHeader setType={stntReport.usage} title={data?.evlNm} />

                <TabMenu
                    className="tab-size1 con-mt3"
                    tabItems={[
                        { tabName: '상세 보기', value: '상세 보기' },
                        { tabName: 'AI 총평', value: 'AI 총평' },
                    ]}
                    selected={stntReport.mainTab}
                    onChange={mainTabChange}
                />
                <div className="con-mt3">
                    <ReportTemplate
                        // 테이블 해더: 항목, 점수(평가기준)
                        headerLeft={<ReportHeaderLeft />}
                        // 테이블 해더: 콘텐츠 정보, 학생 이름
                        headerRight={
                            <ReportHeaderStntRight
                                nickname={nickname}
                                scoreType={scoreType}
                                score={score}
                                submAt={submAt}
                                selected={stntReport.subTab}
                                subTabChange={subTabChange}
                                studentChange={studentChange}
                                // mainTab={stntReport.mainTab}
                                // isStudent={false}
                            />
                        }
                        // 테이블 바디: 썸네일 리스트
                        bodyLeft={
                            stntReport.mainTab !== 'AI 총평' && (
                                <ReportStntThumbList
                                    thumbList={data?.evlInfoList}
                                    selected={stntReport.subTab}
                                    selectedThumbIndex={stntReport.thumbListIdx}
                                    onThumbClick={tumbListChange}
                                />
                            )
                        }
                        // 테이블 바디: 콘텐츠, 뷰어
                        bodyRight={
                            stntReport.mainTab !== 'AI 총평' && (
                                <ReportStntContentsViewer
                                    evlId={targetId}
                                    stntId={stntId}
                                    setsId={stntReport?.setsId}
                                    selectedThumbContent={selectedModule}
                                    selectedContentsNumber={contentsNum}
                                    selectedSubTab={stntReport.subTab}
                                    onRefetch={refetchDetailHandler}
                                />
                            )
                        }
                        bodyTotal={
                            stntReport.mainTab === 'AI 총평' && (
                                <AiReview evalId={targetId} stntId={stntId} />
                            )
                        }
                    />
                </div>
            </div>
        </div>
    );
}

export default RouteComponent;
